import dayjs from "dayjs";
import {
  getStrategyHistory,
  getElinkList,
  getPlan,
  fetchPowerTrackingData,
  queryThirdPower,
  getElinkStruct
} from "./api/index.js";
import { download_get } from "@/api/modules/download/index.js";
import { getChartOption } from "./linesChartOptions.js";

export class PowerTrackingData {
  constructor(stationId, options) {
    this.stationId = stationId; // 当前电站id
    this.eLinkList = []; // 存放当前电站下的所有eLink
    this.strategyList = []; // 存放当前电站下的所有策略

    /** strategySwitch >>> 开关值来自：eCloud/电站管理/电站-基本参数/【策略控制：开关】
     * true: 获取用户在【策略管理/运行策略】界面手动添加的策略。
     * false：获取【由eLink上报】的【策略】
     */
    this.strategySwitch = options.strategySwitch ?? true;
    this.startTime =
      options.startTime ?? dayjs().format("YYYY-MM-DD ") + "00:00:00";
    this.endTime =
      options.endTime ?? dayjs().format("YYYY-MM-DD ") + "23:59:59";

    this.selectType = options.selectType ?? "station"; // station|eLink|eBlock
    this.eLinkId = undefined;
    this.eBlockId = undefined;
    this.meterModel = options.meterModel ?? 1; // 0-虚拟表，1-实体表

    this.chartData = {
      socChartData: [],
      powerChartData: [],
      strategyChartData: [], // 获取【策略】跟踪曲线需要的数据
      isShowStrategyLine: false,
      demand: 0, // 最大需量，
      thirdPowerChartData: [], // 获取【虚拟电厂调度功率】的曲线数据
      isShowThirdPowerLine: false, // 图表是否展示【虚拟电厂调度功率】的曲线
      historyTimePointsChartData: [], // 获取【策略修改点】的曲线数据
      isShowHistoryPoints: false // 图表是否展示【策略修改点】的曲线
    };
    this.isGroupeLink = false;
  }

  /**
   * 初始化图表
   * @param {*} chartObj
   */
  async initCharts(chartObj) {
    try {
      await this.getSocAndPowerTrackData();
      await this.getStrategyAndDemandTrackData();
      await this.getThirdPowerChartDataForVirtualStation();
      await this.getEditStrategyHistoryTimePoints();
      this.getElinkGroupData();
      this.renderCharts(chartObj);
    } catch (err) {
      console.error('功率跟踪-请求图表数据的相关接口-请求异常', err);
      throw err;
    }
  }

  /**
   * 监听eCharts实例的图例点击事件
   * @param {*} chartObj
   */
  listenChartsLegendChange(chartObj) {
    chartObj.on("legendselectchanged", params => {
      if (params.name == "需量保护阈值") {
        // 记录options中需要被编辑的属性
        const editOptions = {
          yAxis: [{}, {}]
        };
        if (params.selected[params.name]) {
          const { min, max } = this.refreshChartYrange(); // 计算Y坐标的范围
          editOptions.yAxis[1]["max"] = max;
          editOptions.yAxis[1]["min"] = min;
          chartObj.setOption(editOptions);
        } else {
          const { min, max } = this.refreshChartYrange(false); // 计算Y坐标的范围 >>> 最大需量不参与
          editOptions.yAxis[1]["max"] = max;
          editOptions.yAxis[1]["min"] = min;
          chartObj.setOption(editOptions);
        }
      }
    });
  }

  /**
   * 更新电站id >>> 直接触发所有数据更新&图表刷新
   * @param {*} id
   */
  async updateStationId(id) {
    this.stationId = id;
    // 更新相关数据
    await this.updateElinkList(id); // 重新获取当前电站下的所有 eLink
    await this.getStrategysByStationId(id); //  重新获取当前电站下的所有 strategyList
  }

  /**
   * 更新当前选择的时间范围
   * @param {*} st
   * @param {*} et
   */
  updateTimeRange(st, et) {
    this.startTime = dayjs(st).format("YYYY-MM-DD ") + "00:00:00";
    this.endTime = dayjs(et).format("YYYY-MM-DD ") + "23:59:59";
  }

  /**
   * 更新当前选择的类型：整站|eLink|eBlock
   * @param {*} type station|eLink|eBlock
   */
  updateSelectType(type) {
    this.selectType = type;
  }

  /**
   * 更新当前选中的eLinkId|eBlockId
   * @param {*} eLinkId
   * @param {*} eBlockId
   */
  updateSelectedDevice(eLinkId, eBlockId) {
    this.eLinkId = eLinkId;
    this.eBlockId = eBlockId;
  }

  /**
   * 更新表类型
   * @param {*} meterModel 0-虚拟表，1-实体表
   */
  updateMeterModel(meterModel) {
    this.meterModel = meterModel;
  }

  /**
   * 更新图表
   * @param {*} chartObj
   */
  async updateCharts(chartObj) {
    await this.initCharts(chartObj);
  }

  /**
   * 执行图表的渲染
   * @param {*} chartObj eChart的实例
   */
  renderCharts(chartObj) {
    this.getElinkGroupData();

    setTimeout(() => {
      const options = getChartOption(
        this.chartData,
        this.startTime,
        this.endTime,
        this.chartData.demand,
        this.isGroupeLink,
        this.selectType,
        this.meterModel
      );
      // 将配置对象设置给eChart进行渲染
      chartObj.clear();
      chartObj.setOption(options);
      // 确保图表正确调整大小
      if (chartObj && typeof chartObj.resize === 'function') {
        chartObj.resize();
      }
    }, 1000)
  }

  /**
   * 导出功率跟踪数据
   * @param {*} st
   * @param {*} et
   * @param {*} selectType
   * @param {*} eLinkId
   * @param {*} eBlockId
   */
  async exportPowerTrackingData(
    st = this.startTime,
    et = this.endTime,
    selectType = this.selectType,
    eLinkId = this.eLinkId,
    eBlockId = this.eBlockId
  ) {
    const url = `/dashboard/dataAnalysis/powerTracking/querySocAndPower/export`;
    const params = {
      stationId: String(this.stationId),
      startTime: st,
      endTime: et
    };
    if (selectType == "station") {
      const elinkIDs = this.getAllELinkId();
      const eblockIDs = this.getAllEBlockId();
      if (elinkIDs.length < 1 || eblockIDs.length < 1) {
        console.error(
          "当前电站下的eLink或eBlock为空，无法正常导出数据, elinkIDs =",
          elinkIDs,
          "eblockIDs =",
          eblockIDs
        );
        window.dispatchNotify({
          type: "info",
          message: `当前电站下的eLink或eBlock为空，无法正常导出数据，请检查！`
        });
        return false;
      }
      params["allElink"] = true;
      params["elinkIDs"] = elinkIDs;
      params["allEblock"] = true;
      params["eblockIDs"] = eblockIDs;
    }
    if (selectType == "eLink") {
      const eblockIDs = this.getEBlockIdsByELinkid(this.eLinkId);
      if (eblockIDs.length < 1) {
        console.error(
          `当前eLink【${this.eLinkId}】下的eBlock为空，无法正常导出数据, eblockIDs =`,
          eblockIDs
        );
        window.dispatchNotify({
          type: "info",
          message: `当前eLink下的eBlockId为空，无法正常导出数据，请检查！`
        });
        return false;
      }
      params["allElink"] = false;
      params["elinkIDs"] = [this.eLinkId];
      params["allEblock"] = true;
      params["eblockIDs"] = eblockIDs;
    }
    if (selectType == "eBlock") {
      params["allElink"] = false;
      params["elinkIDs"] = [eLinkId];
      params["allEblock"] = false;
      params["eblockIDs"] = [eBlockId];
    }

    params.eblockIDs = params.eblockIDs.join(",");
    params.elinkIDs = params.elinkIDs.join(",");

    await download_get(url, params, `power_tracking_${new Date().getTime()}.xlsx`)
  }

  /** ======【获取数据的方法】================================================================================================================= */
  /**
   * 获取【soc】【power】跟踪曲线需要的数据
   */
  async getSocAndPowerTrackData() {
    let params = {
      stationId: String(this.stationId),
      startTime: this.startTime,
      endTime: this.endTime
    };

    if (this.selectType == "station") {
      const elinkIDs = this.getAllELinkId();
      const eblockIDs = this.getAllEBlockId();
      if (elinkIDs.length < 1 || eblockIDs.length < 1) {
        console.error(
          "当前电站下的eLink或eBlock为空，无法正常请求SocAndPower的跟踪数据, elinkIDs =",
          elinkIDs,
          "eblockIDs =",
          eblockIDs
        );
        return false;
      }
      params["allElink"] = true;
      params["elinkIDs"] = [];
      params["allEblock"] = true;
      params["eblockIDs"] = [];
    }
    if (this.selectType == "eLink") {
      const eblockIDs = this.getEBlockIdsByELinkid(this.eLinkId);
      if (eblockIDs.length < 1) {
        console.error(
          `当前eLink【${this.eLinkId}】下的eBlock为空，无法正常请求SocAndPower的跟踪数据, eblockIDs =`,
          eblockIDs
        );
        return false;
      }
      params["allElink"] = false;
      params["elinkIDs"] = [this.eLinkId];
      params["allEblock"] = true;
      params["eblockIDs"] = eblockIDs;
      params.eblockIDs = params.eblockIDs.join(",");
      params.elinkIDs = params.elinkIDs.join(",");
    }
    if (this.selectType == "eBlock") {
      params["allElink"] = false;
      params["elinkIDs"] = [this.eLinkId];
      params["allEblock"] = false;
      params["eblockIDs"] = [this.eBlockId];
    }

    // 只有在是数组的情况下才 join，避免重复 join 字符串
    if (Array.isArray(params.eblockIDs)) {
      params.eblockIDs = params.eblockIDs.join(",");
    }
    if (Array.isArray(params.elinkIDs)) {
      params.elinkIDs = params.elinkIDs.join(",");
    }
    await fetchPowerTrackingData(params).then(res => {
      if (res?.data && Array.isArray(res.data)) {
        const socChartData = [];
        const powerChartData = [];
        res.data.forEach(it => {
          const time = dayjs(it.record_time).format("YYYY-MM-DD HH:mm:ss"); // 采样时间
          const soc = Number(it.batterysystemsoc).toFixed(2);
          const power = Number(it.pcspower_dc).toFixed(2);
          socChartData.push([time, soc]);
          powerChartData.push([time, power]);
        });
        this.chartData.socChartData = socChartData;
        this.chartData.powerChartData = powerChartData;
      }
    });
  }

  /** 获取【策略】【最大需量】跟踪曲线需要的数据 */
  async getStrategyAndDemandTrackData() {
    if (this.selectType == "station") {
      const pormiseList = this.eLinkList.map(it => {
        return this.getStrategyAndDemandByElink(it.uuid);
      });
      await Promise.all(pormiseList).then(resList => {
        this.chartData.demand = resList.reduce((total, item) => {
          return total + item.demand;
        }, 0);
        this.chartData.strategyChartData = [];
        this.chartData.isShowStrategyLine = false;
      });
    } else {
      if (!this.eLinkId) {
        console.error("当前eLinkId为", this.eLinkId + "请联系管理员检查！");
        return false;
      }
      const res = await this.getStrategyAndDemandByElink(this.eLinkId);
      this.chartData.strategyChartData = res.strategyChartData;
      this.chartData.isShowStrategyLine = true;
      this.chartData.demand = res.demand;
    }
  }

  /** 获取【虚拟电厂调度功率】的曲线数据 */
  async getThirdPowerChartDataForVirtualStation() {
    const thirdPowerChartData = [];
    if (this.selectType == "station") {
      this.chartData.thirdPowerChartData = thirdPowerChartData;
      this.chartData.isShowThirdPowerLine = false;
      return false;
    }
    const res = await queryThirdPower(
      this.stationId,
      this.eLinkId,
      this.startTime,
      this.endTime
    );
    if (res?.data && Array.isArray(res.data)) {
      res.data.forEach(it => {
        const t = dayjs(it.record_time).format("YYYY-MM-DD HH:mm:ss");
        const p = it.third_ctl_power;
        thirdPowerChartData.push([t, p]);
      });
    }
    this.chartData.thirdPowerChartData = thirdPowerChartData;
    this.chartData.isShowThirdPowerLine = true;
  }

  /** 获取【策略修改点】的曲线数据 */
  async getEditStrategyHistoryTimePoints() {
    const historyTimePointsChartData = [];
    if (this.selectType == "station") {
      this.chartData.isShowHistoryPoints = false;
      return false;
    } else {
      const res = await getStrategyHistory(
        this.stationId,
        this.eLinkId,
        1,
        9999
      );
      if (
        res?.data?.strategyLogList &&
        Array.isArray(res.data.strategyLogList)
      ) {
        res.data.strategyLogList.forEach(it => {
          if (
            dayjs(it.created_time) >= dayjs(this.startTime) &&
            dayjs(it.created_time) <= dayjs(this.endTime)
          ) {
            const historyTimePoint = dayjs(it.created_time).format(
              "YYYY-MM-DD HH:mm:ss"
            );
            historyTimePointsChartData.push(historyTimePoint);
          }
        });
        this.chartData.isShowHistoryPoints = true;
      }
    }
    this.chartData.historyTimePointsChartData = historyTimePointsChartData;
  }

  /** ======【辅助性功能方法】================================================================================================================= */

  /**
   * 获取给定电站下所有 eLink 数据
   * @param {*} stationId
   */
  async updateElinkList(stationId = this.stationId) {
    const list = await getElinkStruct(stationId);
    this.eLinkList = list?.data && Array.isArray(list.data) ? list.data : [];
  }

  /**
   * 获取电站下多有策略信息
   * @param {*} stationId
   */
  async getStrategysByStationId(stationId = this.stationId) {
    const res = await getElinkList(stationId, 1);
    this.strategyList = (res?.data && Array.isArray(res.data)) ? res.data : [];
  }

  /** 获取当前电站所有eLink的id */
  getAllELinkId() {
    const allELinkList = [];
    this.eLinkList.forEach(eLink => {
      allELinkList.push(eLink.uuid);
    });
    return allELinkList;
  }

  /** 获取当前电站所有eBlock的id */
  getAllEBlockId() {
    const allEBlockList = [];
    this.eLinkList.forEach(eLink => {
      eLink.list.forEach(eBlock => {
        allEBlockList.push(eBlock.uuid);
      });
    });
    return allEBlockList;
  }

  /** 获取当前电站所有eBlock的id */
  getEBlockIdsByELinkid(eLinkId) {
    const eBlockIds = [];
    const eLink = this.eLinkList.find(it => it.uuid == eLinkId);
    if (eLink) {
      eLink.list.forEach(eBlock => {
        eBlockIds.push(eBlock.uuid);
      });
    }
    return eBlockIds;
  }

  /**
   * 合并【相同时间段内】【多个eLink的】策略曲线
   * 采样点格式：['2024-05-26 00:00:00', '100']
   * @param {*} pointList1
   * @param {*} pointList2
   * @returns
   */
  mergeStrategyForStation(pointList1, pointList2) {
    const result = [];

    if (pointList1.length == 0) {
      return pointList2;
    } else if (pointList2.length == 0) {
      return pointList1;
    }
    // 去重：去掉format直方图时的插入点 >>> 确保：[st, et]范围内，相同间隔获取到的采样点数量一致
    const list1 = pointList1.reduce((res, it, idx, originList) => {
      if (idx == 0) {
        res.push(it);
      } else {
        if (originList[idx][0] != originList[idx - 1][0]) {
          res.push(it);
        }
      }
      return res;
    }, []);
    const list2 = pointList2.reduce((res, it, idx, originList) => {
      if (idx == 0) {
        res.push(it);
      } else {
        if (originList[idx][0] != originList[idx - 1][0]) {
          res.push(it);
        }
      }
      return res;
    }, []);
    /** 每个eLink的策略的采样起止时间一样，且采样间隔均为1min */
    list1.forEach((it, idx) => {
      const t = it[0];
      const p = Number(it[1]) + Number(list2[idx][1]);
      result.push([t, p]);
    });

    return result;
  }

  /**
   * 给曲线的拐点插电 >>> 格式化为直方图
   * @param {*} list
   * @returns
   */
  addCornerSamplingPoint(list) {
    const result = [];
    // list.reduce()
    list.forEach((it, idx, originList) => {
      if (idx === 0) {
        result.push(it);
      } else {
        const p = originList[idx - 1][1];
        if (it[1] == p) {
          result.push(it);
        } else {
          result.push([it[0], p]);
          result.push(it);
        }
      }
    });
    return result;
  }

  /**
   * 获取以eLink为单位要展示的【策略曲线】【最大需量曲线】要展示的数据
   * 注意事项：
   * 1、界面上选择展示eBlock曲线数据时：取其所在eLink的相关数据
   * 2、界面上选择展示【整站】曲线数据时：取所有eLink的相关数据的叠加
   * @param {*} eLinkId
   * @param {*} st
   * @param {*} et
   */
  async getStrategyAndDemandByElink(
    eLinkId,
    st = this.startTime,
    et = this.endTime
  ) {
    if (this.strategyList.length < 1) {
      // 策略列表为空，直接返回
      return { strategyChartData: [], demand: 0 };
    }
    const currElinkStrategy = this.strategyList.find(
      it => it.elink_id == eLinkId
    ); // 获取当前eLink下的策略

    /** 获取当前eLink的策略信息
     * 注意：strategySwitch >>> 开关值来自：eCloud/电站管理/电站-基本参数/【策略控制：开关】
     * >>> true: 获取用户在【策略管理/运行策略】界面手动添加的策略。
     * >>> false：获取【由eLink上报】的【策略】
     */
    const strategy = [];
    if (!this.strategySwitch) {
      const strategyJson = currElinkStrategy.strategys_current ?? []; // 1天内的策略,对应每个时间段的信息
      const day = dayjs();
      const oneDayRes = this.generateStrategyChartDataForOneDay(
        day,
        strategyJson
      );
      strategy.push(...oneDayRes);

    } else {
      // 获取起止时间内的所有日期
      const startDay = dayjs(st);
      const endDay = dayjs(et);
      let dayIdx = dayjs(startDay);
      const dayList = [];
      while (dayIdx < endDay) {
        dayList.push(dayIdx);
        dayIdx = dayjs(dayIdx).add(1, "day");
      }

      // 获取每天的策略类型-生成每天的策略数据
      for (const day of dayList) {
        // 1、获取每天的策略类型
        let isRestDay = false;
        if (currElinkStrategy.week_plan == "1") {
          // 单休
          isRestDay = day.day() === 0;
        } else if (currElinkStrategy.week_plan == "2") {
          // 双休
          isRestDay = day.day() === 6 || day.day() === 0;
        } else if (currElinkStrategy.week_plan == "0") {
          // 无休
          isRestDay = false;
        }
        // 每一天的策略类型分为：'work' | 'rest' | '计划优先的情况'
        let strategyType = isRestDay
          ? "strategys_" + "rest"
          : "strategys_" + "work";
        // 判断当天的策略类型是否为【计划优先的情况】 >>> 即：在 策略管理/日历视图/切换策略 手动切换过策略类型的情况。
        const planRes = await getPlan(eLinkId, st, et);
        console.log(planRes)
        if (planRes?.data && Array.isArray(planRes.data)) {
          planRes.data.forEach(plan => {
            let sd = dayjs(plan.strategy_date).format("YYYY-MM-DD 00:00:00");
            let cd = day.format("YYYY-MM-DD 00:00:00");
            if (sd == cd) {
              strategyType = "strategys_" + plan.strategy_type;
            }
          });
        }

        // 2、生成每天的策略数据
        const strategyJson = currElinkStrategy[strategyType] ?? []; // 1天内的策略,对应每个时间段的信息
        const oneDayRes = this.generateStrategyChartDataForOneDay(
          day,
          strategyJson
        );
        strategy.push(...oneDayRes);
      }
    }
    // 格式化策略曲线 >>> 插点 >>> 1min一个采样点
    const strategyChartData = this.addPointToFormatStrategyChartData(
      strategy,
      st,
      et
    );

    const demand = Number(currElinkStrategy?.demand) ?? "--"; // 获取当前eLink的最大需量

    console.log(currElinkStrategy, demand)

    return { strategyChartData, demand };
  }

  /**
   * 给【策略曲线】插入采样点
   * 1、采样间隔为1min
   * 2、处理 00:00:00
   * @param {*} chartData
   * @param {*} st
   * @param {*} et
   * @returns
   */
  addPointToFormatStrategyChartData(chartData, st, et) {
    const result = [];
    const startTime = dayjs(st);
    const endTime = dayjs(et);
    let idxTime = dayjs(st);
    while (idxTime < endTime) {
      const t = idxTime.format("YYYY-MM-DD HH:mm:ss");
      const p = this.findStrategyPowerDataByTime(chartData, t);
      /** 处理拐点 >>> 图表format为直方图
       * 每分钟的point采样时，取值范围为 [st, et]
       * 拐点插入点：[前一段的time, 后一段的power] >>> 连续状态：前一段的结束，即后一段的开始
       */
      const len = result.length;
      if (len > 0) {
        const lastTime = result[len - 1][0];
        const lastPower = result[len - 1][1];
        if (p !== lastPower) {
          result.push([lastTime, p]);
        }
      }
      result.push([t, p]);
      idxTime = idxTime.add(1, "minute");
    }
    return result;
  }

  /**
   * 从【策略列表】中查询【目标时间】对应的功率值
   * @param {*} strategyList 策略列表
   * @param {*} time 目标时间
   * @returns
   */
  findStrategyPowerDataByTime(strategyList, time) {
    const len = strategyList.length;
    for (let i = 0; i < len; i++) {
      const item = strategyList[i];
      const start = dayjs(item.start_time);
      const end = dayjs(item.end_time);
      const targetTime = dayjs(time);
      if (targetTime >= start && targetTime <= end) {
        return item.power;
      }
    }
    console.error(
      dayjs(time).format("YYYY-MM-DD HH:mm:ss") + "对应策略中无功率值"
    );
  }

  /**
   * 生成【1天策略】对应chart曲线需要的数据
   * @param {*} day
   * @param {*} strategyJson
   * @returns
   */
  generateStrategyChartDataForOneDay(day, strategyJson) {
    const result = [];
    const d = dayjs(day).format("YYYY-MM-DD"); // 格式化时间

    strategyJson.forEach(item => {
      const start_time = d + " " + item.start_time; // + ':00'
      const end_time = d + " " + item.end_time; // + ':00'
      result.push({
        start_time: start_time,
        end_time: end_time,
        power: item.power,
        lower_limit: item.lower_limit,
        upper_limit: item.lower_limit
      });
    });
    return result;
  }

  /**
   * 更新图表Y坐标的取值范围
   * @param {*} isShowMaxDemandLine 当前是否展示最大需量
   * @param {*} chartData
   * @returns
   */
  refreshChartYrange(isShowMaxDemandLine = true, chartData = this.chartData) {
    let min = 0;
    let max = 0;
    // 策略
    if (chartData.isShowStrategyLine) {
      chartData.strategyChartData.forEach(it => {
        if (Array.isArray(it) && it.length > 1) {
          const num = Number(it[1]);
          if (num > max) {
            max = num;
          }
          if (num < min) {
            min = num;
          }
        }
      });
    }

    // 虚拟电厂调度功率
    if (chartData.isShowThirdPowerLine) {
      chartData.thirdPowerChartData.forEach(it => {
        if (Array.isArray(it) && it.length > 1) {
          const num = Number(it[1]);
          if (num > max) {
            max = num;
          }
          if (num < min) {
            min = num;
          }
        }
      });
    }

    // 功率
    chartData.powerChartData.forEach(it => {
      if (Array.isArray(it) && it.length > 1) {
        const num = Number(it[1]);
        if (num > max) {
          max = num;
        }
        if (num < min) {
          min = num;
        }
      }
    });

    // 需量
    if (isShowMaxDemandLine) {
      const num = Number(chartData.demand);
      if (num > max) {
        max = num;
      }
      if (num < min) {
        min = num;
      }
    }

    max = parseInt((max + 11) / 10) * 10;
    min = parseInt((min - 1) / 10) * 10;

    if (min == 0 && max == 0) {
      return { min: 0, max: 100 };
    }

    return { min, max };
  }

  /**获取elinkgroup数据
   * 根据组信息判断是否显示总需量
   */

  getElinkGroupData() {
    this.isGroupeLink = false;
  }
}

